import AccountType from 'scripts/constants/AccountType';
import {
  FaBuilding,
  FaChartPie,
  FaHardHat,
  FaHome,
  FaLandmark,
  FaStore,
  FaTractor,
} from 'react-icons/fa';
import {
  MdOutlineArticle,
  MdOutlineDescription,
  MdOutlineFactCheck,
  MdOutlineReceiptLong,
} from 'react-icons/md';
import { FiFileText } from 'react-icons/fi';
import PropertyTypes from 'src/enums/propertyTypes';
import PlanTypes from 'src/enums/planTypes';
import {
  dollarDecimal,
  floatToPercentDecimal,
  longDate,
} from 'src/utils/formats';
import { toPlanName } from 'src/utils/account';
import { csvCell } from '../advisorCsv';
import {
  getAdvisorDateTimestamp,
  normalizeAdvisorDate as normalizeDateValue,
} from '../advisorClientDetailData';

export const FILTER_PLAN_TYPES = [
  PlanTypes.BUILD_YOUR_OWN,
  PlanTypes.SMART_INVEST,
];

const normalizeDisplayDate = (value) => {
  const normalized = normalizeDateValue(value);
  const dayFirstDate =
    typeof normalized === 'string' &&
    normalized.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  return dayFirstDate
    ? `${dayFirstDate[3]}-${dayFirstDate[2]}-${dayFirstDate[1]}T00:00:00+10:00`
    : normalized;
};

export const formatCurrency = (value) =>
  value === null || value === undefined ? '—' : dollarDecimal(value);

export const formatCount = (value) =>
  value === null || value === undefined
    ? '—'
    : Number(value).toLocaleString('en-AU');

export const formatDate = (value) =>
  value ? longDate(normalizeDisplayDate(value)) : '—';

export const formatDateTime = (value) =>
  value
    ? `${formatDate(value)}, ${new Date(
        normalizeDateValue(value),
      ).toLocaleTimeString('en-AU', {
        timeZone: 'Australia/Sydney',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      })}`
    : '—';

export const formatCompactDateTime = (value) =>
  value
    ? new Date(normalizeDateValue(value)).toLocaleString('en-AU', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        timeZone: 'Australia/Sydney',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      })
    : '—';

export const downloadCsv = (fileName, rows) => {
  const csv = rows.map((row) => row.map(csvCell).join(',')).join('\r\n');
  const downloadUrl = window.URL.createObjectURL(
    new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
  );
  const anchor = window.document.createElement('a');
  anchor.href = downloadUrl;
  anchor.download = fileName;
  window.document.body.appendChild(anchor);
  anchor.click();
  window.document.body.removeChild(anchor);
  window.URL.revokeObjectURL(downloadUrl);
};

export const formatShortId = (value) =>
  value ? String(value).split('-')[0].toUpperCase() : '—';

export const isWithinDays = (value, days) => {
  if (!days) return true;
  const timestamp = getAdvisorDateTimestamp(value);
  return (
    Number.isFinite(timestamp) &&
    timestamp >= Date.now() - Number(days) * 24 * 60 * 60 * 1000
  );
};

export const formatPercentage = (value) => {
  if (value === null || value === undefined) return '—';
  const formatted = floatToPercentDecimal(value);
  return Number(value) > 0 ? `+${formatted}` : formatted;
};

export const formatStatus = (value) => {
  if (!value) return null;
  return String(value)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

export const getValueTone = (value) => {
  if (Number(value) > 0) return 'positive';
  if (Number(value) < 0) return 'negative';
  return 'neutral';
};

export const getStatusTone = (value) => {
  const status = String(value || '').toLowerCase();
  if (
    ['failed', 'inactive', 'cancelled', 'rejected'].some((term) =>
      status.includes(term),
    )
  ) {
    return 'negative';
  }
  if (
    [
      'active',
      'approved',
      'completed',
      'done',
      'non-citizen',
      'passed',
      'resident',
    ].some((term) => status.includes(term)) ||
    status === 'no' ||
    status === 'false'
  ) {
    return 'positive';
  }
  return 'info';
};

export const formatPlanType = (value) =>
  toPlanName(value) || formatStatus(value) || 'Account';

export const getAccountTypeTone = (type) => {
  const accountType = String(type || '').toUpperCase();
  if ([AccountType.INDIVIDUAL, AccountType.MINOR].includes(accountType)) {
    return accountType.toLowerCase();
  }
  return [
    AccountType.SMSF,
    AccountType.TRUST,
    AccountType.CORPORATION,
    AccountType.PARTNERSHIP,
    'COMPANY',
  ].includes(accountType)
    ? 'business'
    : 'other';
};

export const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (unused, index) => index + 1);
  }

  const items = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) items.push('start-ellipsis');
  for (let page = start; page <= end; page += 1) items.push(page);
  if (end < totalPages - 1) items.push('end-ellipsis');
  items.push(totalPages);

  return items;
};

export const TRANSACTION_TYPES = {
  Buy: 'positive',
  Sell: 'negative',
  Fee: '',
  Deposit: 'deposit',
  Withdrawal: 'withdrawal',
  Distribution: 'info',
  Other: 'other',
};

const getTransactionActivity = (description) => {
  const text = String(description || '');
  const trade = text.match(
    /^\s*(BUY|SELL)\b(?:\s+[\d,.]+\s+([A-Z]{2,}\d+)\b)?/i,
  );
  if (trade) {
    return {
      label: trade[1].toUpperCase() === 'BUY' ? 'Buy' : 'Sell',
      propertyCode: trade[2] ? trade[2].toUpperCase() : '',
    };
  }
  if (/\bfee\b/i.test(text)) return { label: 'Fee' };
  if (/\bdistribution\b/i.test(text)) {
    const property = text.match(/\bproperty\s+([A-Z]{2,}\d+)\b/i);
    return {
      label: 'Distribution',
      propertyCode: property ? property[1].toUpperCase() : '',
    };
  }
  if (/\bwithdraw(?:al|als|n)?\b/i.test(text)) {
    return { label: 'Withdrawal' };
  }
  if (/\bdeposit\b|\bdirect\s+debit\b.*\bwallet\b/i.test(text)) {
    return { label: 'Deposit' };
  }
  return { label: 'Other' };
};

export const getTransactionsWithActivity = (transactions) => {
  const activities = transactions.map((transaction) => ({
    ...transaction,
    activity: getTransactionActivity(transaction.description),
  }));
  const tradeProperties = new Map();
  activities.forEach((transaction) => {
    if (
      transaction.externalId &&
      ['Buy', 'Sell'].includes(transaction.activity.label) &&
      transaction.activity.propertyCode
    ) {
      tradeProperties.set(
        transaction.externalId,
        transaction.activity.propertyCode,
      );
    }
  });
  return activities.map((transaction) => {
    const propertyCode =
      transaction.externalId && tradeProperties.get(transaction.externalId);
    return transaction.activity.label === 'Fee' && propertyCode
      ? {
          ...transaction,
          activity: { ...transaction.activity, propertyCode },
        }
      : transaction;
  });
};

export const ORDERS_PROPERTY_ICONS = {
  [PropertyTypes.house]: FaHome,
  [PropertyTypes.multihouse]: FaHome,
  [PropertyTypes.unit]: FaBuilding,
  [PropertyTypes.multiunit]: FaBuilding,
  [PropertyTypes.farm]: FaTractor,
  [PropertyTypes.offices]: FaBuilding,
  [PropertyTypes.commercial]: FaStore,
  [PropertyTypes.development]: FaHardHat,
  [PropertyTypes.securedDebt]: FaLandmark,
  [PropertyTypes.unsecuredDebt]: FaLandmark,
  [PropertyTypes.diversifiedDebt]: FaLandmark,
  [PropertyTypes.equityFund]: FaChartPie,
  [PropertyTypes.debtFund]: FaChartPie,
  [PropertyTypes.diversifiedFund]: FaChartPie,
};

export const formatFinancialYearRange = (year) =>
  year ? `1 July ${year - 1} - 30 June ${year}` : '';

export const getDocumentIcon = (key) => {
  const documentKey = String(key || '').toLowerCase();
  if (documentKey.includes('portfolio')) return MdOutlineDescription;
  if (documentKey.includes('annual')) return MdOutlineArticle;
  if (documentKey.includes('tax')) return MdOutlineReceiptLong;
  if (documentKey.includes('wholesale')) return MdOutlineFactCheck;
  return FiFileText;
};
